// src/pages/Admin/AdherentPret/EditAdherentPretModal.jsx
import React, { useEffect, useState } from "react";
import { FaSave, FaTimes, FaCalendarCheck, FaEraser } from "react-icons/fa";
import { updateAdherentPret } from "../../../services/adherentpretServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import moment from "moment";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function EditAdherentPretModal({
  isOpen,
  onClose,
  pret,
  refreshList,
}) {
  const [formData, setFormData] = useState({ date_remise: "", note_pret: "" });
  const [submitting, setSubmitting] = useState(false);
  const hasValidReturnDate = moment(
    formData.date_remise,
    "YYYY-MM-DD",
    true
  ).isValid();

  useEffect(() => {
    if (!isOpen || !pret) return;
    setFormData({
      date_remise: pret?.date_remise
        ? moment(pret.date_remise).format("YYYY-MM-DD")
        : "",
      note_pret: pret?.note_pret || "",
    });
    setSubmitting(false);
  }, [isOpen, pret]);

  if (!isOpen || !pret) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const setToday = () =>
    setFormData((p) => ({ ...p, date_remise: todayStr() }));
  const clearDate = () => setFormData((p) => ({ ...p, date_remise: "" }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        date_remise: hasValidReturnDate ? formData.date_remise : null, // null si non valide / non renseignée
        note_pret: formData.note_pret?.trim() || null,
      };
      await updateAdherentPret(pret.id, payload);
      toast.success("Prêt mis à jour avec succès");
      refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du prêt", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSubmitting(false);
    }
  };

  const datePretLabel = moment(pret.date_pret).isValid()
    ? moment(pret.date_pret).format("DD/MM/YY")
    : "Inconnue";

  const status = hasValidReturnDate
    ? `Rendu le ${moment(formData.date_remise).format("DD/MM/YY")}`
    : "En cours"; // ou "Non restitué" si tu préfères

  const hasPhoto = !!pret?.article?.photo_url;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Modifier le prêt
          </h3>
        </div>

        {/* Corps */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
          {/* Résumé prêt */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900 p-4">
            <div className="flex items-start gap-4">
              {hasPhoto && (
                <img
                  src={pret.article.photo_url}
                  alt={pret.article?.nom || "Matériel"}
                  className="h-16 w-16 rounded-lg object-cover ring-2 ring-slate-200 dark:ring-slate-700"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  <span className="font-semibold text-slate-800 dark:text-slate-100">
                    Adhérent :
                  </span>{" "}
                  {pret.adherent?.nom} {pret.adherent?.prenom}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  <span className="font-semibold text-slate-800 dark:text-slate-100">
                    Matériel :
                  </span>{" "}
                  {pret.article?.nom}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  <span className="font-semibold text-slate-800 dark:text-slate-100">
                    Date du prêt :
                  </span>{" "}
                  {datePretLabel}
                </p>
                <div className="mt-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium
                    ${
                      hasValidReturnDate
                        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                        : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                    }`}
                  >
                    {status}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Date de remise + raccourcis */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Date de remise (laisser vide pour “En cours”)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                name="date_remise"
                value={formData.date_remise}
                onChange={handleChange}
                min={
                  moment(pret.date_pret).isValid()
                    ? moment(pret.date_pret).format("YYYY-MM-DD")
                    : undefined
                }
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />

              <button
                type="button"
                onClick={setToday}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                title="Définir à aujourd’hui"
              >
                <FaCalendarCheck /> Aujourd’hui
              </button>
              <button
                type="button"
                onClick={clearDate}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                title="Effacer la date"
              >
                <FaEraser /> Effacer
              </button>
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Note (optionnelle)
            </label>
            <input
              type="text"
              name="note_pret"
              placeholder="Ex : état restitué, accessoires manquants, etc."
              value={formData.note_pret}
              onChange={handleChange}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              className={`!rounded-xl gap-2 ${
                submitting ? "opacity-60 pointer-events-none" : ""
              }`}
              disabled={submitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
